import React, { useState, useEffect, useCallback, useRef } from 'react';
import Editor from '@monaco-editor/react';
import {
  listEndpoints, getEndpoint, createEndpoint, updateEndpoint, toggleEndpoint,
  regenerateEndpointSlug, deleteEndpoint, listEndpointLogs, clearEndpointLogs,
  getEndpointErrorLog, clearEndpointErrorLog,
} from '../../../api/client';
import { copyToClipboard, formatDate } from '../../../utils/helpers';
import './tabs.css';
import './EndpointsTab.css';

const LANGUAGES = [
  {
    value: 'php', label: 'PHP', monaco: 'php', color: '#777bb3',
    help: 'Available variables: $method, $headers, $query, $body, $json, $bot, $panel ' +
          "($bot has bId, bName, bToken, bPhoneNumberId, bWhatsappId, bWebhookToken; " +
          "$panel is your bot's Panel key/value pairs — the same store command JSON " +
          "reads via {{panel.key}} — so you can keep API keys and secrets out of the " +
          "endpoint code itself). " +
          'echo your response — defaults to JSON / status 200. Use header() and ' +
          'http_response_code() to customize either one, just like a normal PHP page. ' +
          "Call debug_log('message', $data) anywhere in your code to write a line to " +
          "this call's Debug Log below — no file access needed, and plain old " +
          'error_log(...) is captured the same way if you already use that.',
    template:
`<?php
// Available: $method, $headers, $query, $body, $json, $bot, $panel
// $bot   = ['bId','bName','bToken','bPhoneNumberId','bWhatsappId','bWebhookToken']
// $panel = your bot's Panel key/value pairs, e.g. $panel['api_key']

// Anything you pass to debug_log() shows up in this endpoint's
// "Logs" → Debug Log panel for the matching call, with a timestamp.
debug_log('endpoint hit', ['method' => $method, 'query' => $query]);

header('Content-Type: application/json');
echo json_encode([
    'status'   => 'ok',
    'method'   => $method,
    'received' => $json,
]);
`,
  },
  {
    value: 'json', label: 'JSON', monaco: 'json', color: '#2563eb',
    help: 'A static JSON document. Returned exactly as written, every time this endpoint is called — no code runs.',
    template: `{
  "data": {
    "status": "active",
    "version": "v3.0"
  }
}`,
  },
  {
    value: 'javascript', label: 'JavaScript', monaco: 'javascript', color: '#caa800',
    help: 'Coming soon — JavaScript execution needs a Node runtime, which isn\u2019t available on shared hosting yet. ' +
          'You can save this, but calls will get a 501 response until it ships.',
    template: `// Coming soon on this hosting plan.
// Saved, but the endpoint will respond with 501 until JS execution ships.
`,
  },
  {
    value: 'python', label: 'Python', monaco: 'python', color: '#3776ab',
    help: 'Coming soon — Python execution needs a Python runtime, which isn\u2019t available on shared hosting yet. ' +
          'You can save this, but calls will get a 501 response until it ships.',
    template: `# Coming soon on this hosting plan.
# Saved, but the endpoint will respond with 501 until Python execution ships.
`,
  },
  {
    value: 'other', label: 'Other (plain text)', monaco: 'plaintext', color: '#6b7280',
    help: 'Returned exactly as written, with a text/plain response.',
    template: 'OK',
  },
];

const langMeta = (value) => LANGUAGES.find(l => l.value === value) || LANGUAGES[0];

const FILE_EXT = { php: 'php', json: 'json', javascript: 'js', python: 'py', other: 'txt' };
const fileNameFor = (lang) => `endpoint.${FILE_EXT[lang] || 'txt'}`;

/** lDebugLog is stored as a JSON array of {t, src, msg, data?}. Falls back to
 *  treating the raw string as one line if it's ever not valid JSON. */
function parseDebugLog(raw) {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [{ t: null, src: 'app', msg: raw }];
  }
}

function isLogError(log) {
  return !!log.lError || (log.lStatusCode && log.lStatusCode >= 400);
}

function logMatchesFilter(log, filter) {
  const error = isLogError(log);
  if (filter.status === 'ok' && error) return false;
  if (filter.status === 'error' && !error) return false;
  if (filter.q) {
    const q = filter.q.toLowerCase();
    const haystack = [
      log.lMethod, log.lStatusCode, log.lError, log.lRequestBody, log.lResponseBody,
      log.lWarnings, log.lDebugLog, log.lQueryString, log.lHeaders,
    ].filter(Boolean).join(' ').toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  return true;
}

function formatDebugLineForExport(entry) {
  const t = entry.t != null ? `+${entry.t}ms` : '      ';
  const src = entry.src === 'error_log' ? 'PHP' : 'APP';
  return `    [${t}] [${src}] ${entry.msg}${entry.data ? ' ' + entry.data : ''}`;
}

function formatLogForExport(log) {
  const error = isLogError(log);
  const lines = [
    `[${formatDate(log.lCreatedAt)}] ${log.lMethod || 'GET'} · ${log.lStatusCode || '—'} · ` +
    `${log.lDurationMs != null ? log.lDurationMs + 'ms' : '—'} · ${error ? 'ERROR' : 'OK'}`,
  ];
  if (log.lError) lines.push(`  Error: ${log.lError}`);
  if (log.lStackTrace) lines.push(`  Stack trace: ${log.lStackTrace}`);
  const debugEntries = parseDebugLog(log.lDebugLog);
  if (debugEntries.length) {
    lines.push('  Debug Log:');
    debugEntries.forEach((e) => lines.push(formatDebugLineForExport(e)));
  }
  if (log.lWarnings) {
    lines.push('  Warnings:');
    try {
      const w = JSON.parse(log.lWarnings);
      (Array.isArray(w) ? w : [log.lWarnings]).forEach((line) => lines.push(`    ${line}`));
    } catch {
      lines.push(`    ${log.lWarnings}`);
    }
  }
  lines.push(`  Request:  ${log.lRequestBody || '(empty)'}`);
  lines.push(`  Response: ${log.lResponseBody || '(empty)'}`);
  return lines.join('\n');
}

function StatusToggle({ active, onChange }) {
  return (
    <button
      type="button"
      className={`ep-status-toggle ${active ? 'is-active' : 'is-disabled'}`}
      onClick={() => onChange(!active)}
      title={active ? 'Active — click to disable' : 'Disabled — click to enable'}
    >
      <span className="ep-status-dot" />
      {active ? 'Active' : 'Disabled'}
    </button>
  );
}

export default function EndpointsTab({ bot }) {
  const bId = bot?.bId;

  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [editing, setEditing] = useState(null);   // endpoint object being edited, or {} for new
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false); // variables/help text is collapsed by default to leave room for code
  const [editorHeight, setEditorHeight] = useState(420); // resizable, same pattern as CommandsTab's code editor
  const savedCodeRef = useRef({ eName: '', eLanguage: '', eCode: '' }); // baseline to detect "no changes"
  const editorRef = useRef(null);   // Monaco editor instance, for undo/redo + focus

  function onEditorResizeMouseDown(e) {
    e.preventDefault();
    const startY = e.clientY, startH = editorHeight;
    const onMove = (mv) => setEditorHeight(Math.max(200, Math.min(800, startH + mv.clientY - startY)));
    const onUp   = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  const [logsFor, setLogsFor] = useState(null);   // endpoint object whose logs are open
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [logFilter, setLogFilter] = useState({ status: 'all', q: '' });
  const [liveTail, setLiveTail] = useState(false);

  // Raw, cPanel-style error_log file for the endpoint's own folder —
  // separate view from the structured per-call logs above.
  const [logsView, setLogsView] = useState('calls'); // 'calls' | 'raw'
  const [rawLog, setRawLog] = useState('');
  const [rawLogLoading, setRawLogLoading] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  // Replaces window.confirm with an in-app modal so it matches the rest of
  // the UI and behaves consistently on mobile.
  const [confirmState, setConfirmState] = useState(null);
  const requestConfirm = ({ title = 'Are you sure?', message, confirmLabel = 'Confirm', danger = false, onConfirm }) => {
    setConfirmState({ title, message, confirmLabel, danger, onConfirm });
  };
  const runConfirm = async () => {
    if (!confirmState) return;
    const { onConfirm } = confirmState;
    setConfirmState((s) => ({ ...s, busy: true }));
    try {
      await onConfirm();
    } finally {
      setConfirmState(null);
    }
  };

  const load = useCallback(async () => {
    if (!bId) return;
    setLoading(true);
    try {
      const { data } = await listEndpoints(bId);
      setEndpoints(data.endpoints || []);
    } catch (e) {
      showToast(e?.response?.data?.message || 'Failed to load endpoints', 'error');
    } finally {
      setLoading(false);
    }
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const fresh = {
      eId: null,
      eName: '',
      eLanguage: 'php',
      eCode: langMeta('php').template,
    };
    savedCodeRef.current = { eName: fresh.eName, eLanguage: fresh.eLanguage, eCode: fresh.eCode };
    setEditing(fresh);
  };

  const openEdit = async (ep) => {
    // The list response is intentionally lightweight and doesn't include eCode,
    // so fetch the full record (with code) before opening the editor.
    setEditing({ ...ep, eCode: '' , __loading: true });
    try {
      const { data } = await getEndpoint(bId, ep.eId);
      savedCodeRef.current = {
        eName: data.endpoint.eName, eLanguage: data.endpoint.eLanguage, eCode: data.endpoint.eCode,
      };
      setEditing({ ...data.endpoint, __loading: false });
    } catch (e) {
      showToast(e?.response?.data?.message || 'Failed to load endpoint code', 'error');
      setEditing(null);
    }
  };

  const handleLanguageChange = (lang) => {
    setEditing((prev) => {
      const isUntouched = !prev.eId && (prev.eCode === '' || prev.eCode === langMeta(prev.eLanguage).template);
      return {
        ...prev,
        eLanguage: lang,
        eCode: isUntouched ? langMeta(lang).template : prev.eCode,
      };
    });
  };

  const save = async () => {
    if (!editing.eName?.trim()) { showToast('Give this endpoint a name', 'error'); return; }

    // If editing an existing endpoint and nothing actually changed, don't round-trip to the API.
    if (editing.eId) {
      const base = savedCodeRef.current;
      const unchanged = base.eName === editing.eName
        && base.eLanguage === editing.eLanguage
        && base.eCode === editing.eCode;
      if (unchanged) {
        showToast('No changes to save');
        setEditing(null);
        return;
      }
    }

    setSaving(true);
    try {
      if (editing.eId) {
        await updateEndpoint(bId, editing.eId, {
          eName: editing.eName, eLanguage: editing.eLanguage, eCode: editing.eCode,
        });
        showToast('Endpoint saved');
      } else {
        await createEndpoint(bId, {
          eName: editing.eName, eLanguage: editing.eLanguage, eCode: editing.eCode,
        });
        showToast('Endpoint created');
      }
      savedCodeRef.current = { eName: editing.eName, eLanguage: editing.eLanguage, eCode: editing.eCode };
      setEditing(null);
      load();
    } catch (e) {
      showToast(e?.response?.data?.message || 'Failed to save endpoint', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveRef = useRef(save);
  saveRef.current = save;

  const handleToggle = async (ep, active) => {
    try {
      await toggleEndpoint(bId, ep.eId, active);
      setEndpoints((prev) => prev.map(e => e.eId === ep.eId ? { ...e, eStatus: active ? 'active' : 'disabled' } : e));
    } catch {
      showToast('Failed to update status', 'error');
    }
  };

  const handleRegenerate = (ep) => {
    requestConfirm({
      title: 'Regenerate URL?',
      message: 'The old URL will stop working immediately — update it anywhere it\u2019s in use first.',
      confirmLabel: 'Regenerate URL',
      danger: true,
      onConfirm: async () => {
        try {
          const { data } = await regenerateEndpointSlug(bId, ep.eId);
          setEndpoints((prev) => prev.map(e => e.eId === ep.eId ? { ...e, url: data.url } : e));
          showToast('New URL generated');
        } catch {
          showToast('Failed to regenerate URL', 'error');
        }
      },
    });
  };

  const handleDelete = (ep) => {
    requestConfirm({
      title: 'Delete endpoint?',
      message: <>Delete <strong>"{ep.eName}"</strong>? This can{'\u2019'}t be undone.</>,
      confirmLabel: 'Delete Endpoint',
      danger: true,
      onConfirm: async () => {
        try {
          await deleteEndpoint(bId, ep.eId);
          setEndpoints((prev) => prev.filter(e => e.eId !== ep.eId));
          showToast('Endpoint deleted');
        } catch {
          showToast('Failed to delete endpoint', 'error');
        }
      },
    });
  };

  const openLogs = async (ep) => {
    setLogsFor(ep);
    setLogsLoading(true);
    setExpandedLog(null);
    // Pre-filter to errors when the last call was an error — the user is
    // opening logs specifically to debug, so show them what went wrong first.
    setLogFilter({ status: ep.eLastStatus === 'error' ? 'error' : 'all', q: '' });
    setLiveTail(false);
    setLogsView('calls');
    setRawLog('');
    try {
      const { data } = await listEndpointLogs(bId, ep.eId);
      setLogs(data.logs || []);
    } catch {
      showToast('Failed to load logs', 'error');
    } finally {
      setLogsLoading(false);
    }
  };

  const closeLogs = () => {
    setLogsFor(null);
    setLiveTail(false);
  };

  const loadRawLog = useCallback(async (silent = false) => {
    if (!logsFor) return;
    if (!silent) setRawLogLoading(true);
    try {
      const { data } = await getEndpointErrorLog(bId, logsFor.eId);
      setRawLog(data.log || '');
    } catch {
      if (!silent) showToast('Failed to load error log', 'error');
    } finally {
      if (!silent) setRawLogLoading(false);
    }
  }, [bId, logsFor]);

  const switchLogsView = (view) => {
    setLogsView(view);
    if (view === 'raw' && !rawLog) loadRawLog();
  };

  const handleCopyRawLog = () => {
    copyToClipboard(rawLog || '');
    showToast('Error log copied');
  };

  const handleDownloadRawLog = () => {
    if (!logsFor) return;
    const blob = new Blob([rawLog || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(logsFor.eName || 'endpoint').replace(/[^a-z0-9-_]+/gi, '_')}-error_log.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClearRawLog = () => {
    if (!logsFor) return;
    requestConfirm({
      title: 'Clear error log?',
      message: 'Clear the error_log file for this endpoint? This can\u2019t be undone.',
      confirmLabel: 'Clear Error Log',
      danger: true,
      onConfirm: async () => {
        try {
          await clearEndpointErrorLog(bId, logsFor.eId);
          setRawLog('');
          showToast('Error log cleared');
        } catch {
          showToast('Failed to clear error log', 'error');
        }
      },
    });
  };

  // Quiet background refresh used by Live Tail — no loading spinner,
  // so the list doesn't flicker every poll.
  const refreshLogsSilently = useCallback(async () => {
    if (!logsFor) return;
    try {
      const { data } = await listEndpointLogs(bId, logsFor.eId);
      setLogs(data.logs || []);
    } catch {
      /* stay silent — a transient failure here shouldn't toast every 4s */
    }
  }, [bId, logsFor]);

  useEffect(() => {
    if (!logsFor || !liveTail) return;
    const id = setInterval(() => {
      if (logsView === 'raw') loadRawLog(true);
      else refreshLogsSilently();
    }, 4000);
    return () => clearInterval(id);
  }, [logsFor, liveTail, logsView, refreshLogsSilently, loadRawLog]);

  const handleDownloadLogs = () => {
    if (!logsFor) return;
    const body = filteredLogs.map(formatLogForExport).join('\n\n');
    const blob = new Blob([body || 'No logs match the current filter.'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(logsFor.eName || 'endpoint').replace(/[^a-z0-9-_]+/gi, '_')}-logs.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClearLogs = () => {
    if (!logsFor) return;
    requestConfirm({
      title: 'Clear all logs?',
      message: 'Clear all logs for this endpoint? This can\u2019t be undone.',
      confirmLabel: 'Clear Logs',
      danger: true,
      onConfirm: async () => {
        try {
          await clearEndpointLogs(bId, logsFor.eId);
          setLogs([]);
          showToast('Logs cleared');
        } catch {
          showToast('Failed to clear logs', 'error');
        }
      },
    });
  };

  const handleTest = async (ep) => {
    showToast('Calling endpoint…', 'info');
    try {
      const res = await fetch(ep.url, { method: 'GET' });
      const text = await res.text();
      window.alert(`HTTP ${res.status}\n\n${text.slice(0, 2000)}`);
    } catch (e) {
      window.alert('Request failed: ' + e.message);
    }
  };

  const errorCount = logs.filter(isLogError).length;
  const okCount = logs.length - errorCount;
  const filteredLogs = logs.filter((log) => logMatchesFilter(log, logFilter));

  if (!bId) return null;

  return (
    <div className="tab-content endpoints-tab">
      <div className="tab-toolbar">
        <div>
          <h3 className="tab-title">Endpoints</h3>
          <p className="tab-sub">
            Write the backend logic for this bot directly here — no separate server needed.
            Use the generated URL as the <strong>Endpoint URI</strong> on a Flow, or as a
            general-purpose webhook for anything else.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ New Endpoint</button>
      </div>

      {loading ? (
        <div className="tab-loading">Loading endpoints…</div>
      ) : endpoints.length === 0 ? (
        <div className="tab-empty">
          <p>No custom endpoints yet.</p>
          <button className="btn btn-outline" onClick={openNew}>Create your first endpoint</button>
        </div>
      ) : (
        <div className="ep-list">
          {endpoints.map((ep) => {
            const meta      = langMeta(ep.eLanguage);
            const hasError  = ep.eLastStatus === 'error';
            const neverRan  = !ep.eLastRunAt;
            return (
              <div className={`ep-row${hasError ? ' ep-row-has-error' : ''}`} key={ep.eId}>
                <div className="ep-row-main">
                  <div className="ep-row-top">
                    <span className="ep-lang-badge" style={{ background: meta.color }}>{meta.label}</span>
                    <span className="ep-name">{ep.eName}</span>
                    {hasError && (
                      <span className="ep-error-badge" title="Last call returned an error — open Logs to inspect">
                        ⚠ Error
                      </span>
                    )}
                    <StatusToggle active={ep.eStatus === 'active'} onChange={(v) => handleToggle(ep, v)} />
                  </div>
                  <div className="ep-url-row">
                    <code className="ep-url">{ep.url}</code>
                    <button className="ep-icon-btn" title="Copy URL" onClick={() => { copyToClipboard(ep.url); showToast('URL copied'); }}>📋</button>
                  </div>
                  <div className="ep-meta-row">
                    {neverRan
                      ? <span className="ep-last-run ep-never">Never called yet</span>
                      : <span className={`ep-last-run ep-last-${ep.eLastStatus || 'ok'}`}>
                          <span className={`ep-status-dot-inline ep-status-dot-${ep.eLastStatus === 'error' ? 'error' : 'ok'}`} />
                          Last call: {formatDate(ep.eLastRunAt)}
                          <span className={`ep-status-pill ep-status-pill-${ep.eLastStatus === 'error' ? 'error' : 'ok'}`}>
                            {ep.eLastStatus === 'error' ? 'ERROR' : 'OK'}
                          </span>
                        </span>
                    }
                  </div>
                </div>
                <div className="ep-row-actions">
                  <button className="btn btn-outline btn-sm" onClick={() => handleTest(ep)}>Test</button>
                  <button
                    className={`btn btn-sm${hasError ? ' ep-logs-btn-error' : ' btn-outline'}`}
                    onClick={() => openLogs(ep)}
                    title={hasError ? 'This endpoint has errors — click to inspect logs' : 'View call logs'}
                  >
                    {hasError ? '⚠ View Logs' : 'Logs'}
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={() => openEdit(ep)}>Edit</button>
                  <button className="ep-icon-btn" title="Regenerate URL" onClick={() => handleRegenerate(ep)}>🔄</button>
                  <button className="ep-icon-btn ep-danger" title="Delete" onClick={() => handleDelete(ep)}>🗑️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Create / Edit modal ─────────────────────────────── */}
      {editing && (
        <div className="modal-backdrop" onClick={() => !saving && setEditing(null)}>
          <div
            className="modal modal-lg ep-editor-modal"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                if (!saving && !editing.__loading) saveRef.current();
              }
            }}
          >
            <div className="modal-header">
              <div className="modal-title-group">
                <span className="modal-title-icon">{langMeta(editing.eLanguage).icon || '{ }'}</span>
                <div>
                  <div className="modal-title">{editing.eId ? 'Edit Endpoint' : 'New Endpoint'}</div>
                  <div className="modal-subtitle">
                    {editing.eId ? `Editing "${editing.eName}"` : 'Write the backend logic for this bot'}
                  </div>
                </div>
              </div>
              <button className="modal-close" onClick={() => setEditing(null)}>✕</button>
            </div>

            <div className="modal-body ep-editor-body">
              <div className="ep-editor-fields-compact">
                <input
                  className="form-input ep-name-input"
                  placeholder="Endpoint name, e.g. flow-data-exchange"
                  value={editing.eName}
                  onChange={(e) => setEditing({ ...editing, eName: e.target.value })}
                />
                <div className="ep-lang-picker">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.value}
                      type="button"
                      className={`ep-lang-pill ${editing.eLanguage === l.value ? 'is-selected' : ''}`}
                      style={editing.eLanguage === l.value ? { borderColor: l.color, color: l.color } : {}}
                      onClick={() => handleLanguageChange(l.value)}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="ep-help-toggle"
                  onClick={() => setShowHelp((v) => !v)}
                  title="Show available variables"
                >
                  {showHelp ? 'Hide help ▲' : 'Variables & help ▼'}
                </button>
              </div>
              {showHelp && <p className="ep-help-text">{langMeta(editing.eLanguage).help}</p>}

              <div className="ep-monaco-wrap">
                <div className="ep-editor-titlebar">
                  <div className="ep-editor-dots">
                    <span className="ep-editor-dot ep-editor-dot-red" />
                    <span className="ep-editor-dot ep-editor-dot-yellow" />
                    <span className="ep-editor-dot ep-editor-dot-green" />
                  </div>
                  <div className="ep-editor-tabs">
                    <span className="ep-editor-tab is-active">
                      <span
                        className="ep-editor-tab-dot"
                        style={{ background: langMeta(editing.eLanguage).color }}
                      />
                      {fileNameFor(editing.eLanguage)}
                    </span>
                  </div>
                </div>
                <div className="ep-monaco-editor-area" style={{ height: editorHeight }}>
                  {editing.__loading ? (
                    <div className="ep-monaco-loading">Loading code...</div>
                  ) : (
                    <Editor
                      height="100%"
                      language={langMeta(editing.eLanguage).monaco}
                      theme="vs-dark"
                      value={editing.eCode}
                      onChange={(value) => setEditing({ ...editing, eCode: value ?? '' })}
                      onMount={(editorInstance, monacoInstance) => {
                        editorRef.current = editorInstance;
                        // Ctrl/Cmd+S — save without triggering the browser's "Save page" dialog
                        editorInstance.addCommand(
                          monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyS,
                          () => saveRef.current()
                        );
                        // Ctrl/Cmd+Z and Ctrl/Cmd+Y (or Shift+Z) — undo/redo, made explicit since
                        // focus inside a modal can otherwise lose these to the page.
                        editorInstance.addCommand(
                          monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyZ,
                          () => editorInstance.trigger('keyboard', 'undo', null)
                        );
                        editorInstance.addCommand(
                          monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyY,
                          () => editorInstance.trigger('keyboard', 'redo', null)
                        );
                        editorInstance.addCommand(
                          monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyZ,
                          () => editorInstance.trigger('keyboard', 'redo', null)
                        );
                      }}
                      options={{
                        fontSize: 13,
                        minimap: { enabled: false },
                        automaticLayout: true,
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        tabSize: 4,
                        padding: { top: 10 },
                      }}
                    />
                  )}
                </div>
                <div className="ep-editor-statusbar">
                  <span className="ep-editor-statusbar-lang">
                    {langMeta(editing.eLanguage).label}
                  </span>
                  <span className="ep-editor-statusbar-item">UTF-8</span>
                  <span className="ep-editor-statusbar-item">LF</span>
                  <span className="ep-editor-statusbar-item ep-editor-statusbar-right">
                    {editing.eCode ? editing.eCode.split('\n').length : 1} lines
                  </span>
                </div>
                {/* Drag to resize, same affordance as the command editor */}
                <div
                  className="ep-monaco-resize-handle"
                  onMouseDown={onEditorResizeMouseDown}
                  title="Drag to resize editor"
                  role="separator"
                  aria-label="Resize editor"
                />
              </div>

              {editing.eId && (
                <div className="ep-url-row ep-url-row-modal">
                  <span className="ep-help-label">URL:</span>
                  <code className="ep-url">{editing.url}</code>
                  <button className="ep-icon-btn" title="Copy URL" onClick={() => { copyToClipboard(editing.url); showToast('URL copied'); }}>📋</button>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || editing.__loading}>
                {saving ? 'Saving...' : 'Save Endpoint'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Logs modal ───────────────────────────────────────── */}
      {logsFor && (
        <div className="modal-backdrop" onClick={closeLogs}>
          <div className="modal modal-lg ep-logs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header ep-logs-modal-header">
              <div className="ep-logs-modal-title-wrap">
                <h3 className="modal-title">{logsFor.eName}</h3>
                {!logsLoading && logs.length > 0 && (
                  <div className="ep-logs-modal-summary">
                    {errorCount > 0 && (
                      <span className="ep-summary-chip ep-summary-chip-error">
                        ⚠ {errorCount} error{errorCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    <span className="ep-summary-chip ep-summary-chip-ok">
                      ✓ {okCount} ok
                    </span>
                    <span className="ep-summary-chip ep-summary-chip-total">
                      {logs.length} total
                    </span>
                  </div>
                )}
              </div>
              <button className="modal-close" onClick={closeLogs}>✕</button>
            </div>
            <div className="ep-logs-view-switch">
              <button
                type="button"
                className={`ep-filter-chip ${logsView === 'calls' ? 'is-active' : ''}`}
                onClick={() => switchLogsView('calls')}
              >
                Call Logs
              </button>
              <button
                type="button"
                className={`ep-filter-chip ${logsView === 'raw' ? 'is-active' : ''}`}
                onClick={() => switchLogsView('raw')}
              >
                Error Log (raw)
              </button>
            </div>
            {logsView === 'raw' ? (
              <div className="modal-body">
                <div className="ep-logs-toolbar ep-logs-toolbar">
                  <p className="tab-sub" style={{ margin: 0 }}>
                    The persistent <code>error_log</code> file in this endpoint's own folder —
                    every <code>error_log()</code> call and PHP warning/fatal, in the order it happened,
                    across every call. Cleared independently from the structured Call Logs above.
                  </p>
                  <div className="ep-logs-toolbar-right">
                    <button
                      type="button"
                      className={`ep-live-toggle ${liveTail ? 'is-live' : ''}`}
                      onClick={() => setLiveTail((v) => !v)}
                      title={liveTail ? 'Auto-refreshing every 4s — click to stop' : 'Auto-refresh this log every 4s'}
                    >
                      <span className="ep-live-dot" />
                      {liveTail ? 'Live' : 'Live tail'}
                    </button>
                    <button className="btn btn-outline btn-sm" onClick={() => loadRawLog()}>Refresh</button>
                    <button className="btn btn-outline btn-sm" onClick={handleCopyRawLog}>Copy</button>
                    <button className="btn btn-outline btn-sm" onClick={handleDownloadRawLog}>Download</button>
                    <button className="btn btn-outline btn-sm" onClick={handleClearRawLog}>Clear</button>
                  </div>
                </div>
                {rawLogLoading ? (
                  <div className="tab-loading">Loading error log…</div>
                ) : !rawLog ? (
                  <div className="tab-empty"><p>error_log is empty — nothing's been written to it yet.</p></div>
                ) : (
                  <div className="ep-console">
                    <div className="ep-console-body">
                      {rawLog.split(/\r?\n/).filter((l) => l !== '').map((line, i) => (
                        <div className="ep-console-line" key={i}>
                          <span className="ep-console-msg">{line}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
            <div className="modal-body">
              <div className="tab-toolbar ep-logs-toolbar">
                <div className="ep-logs-toolbar-left">
                  <div className="ep-filter-chips">
                    <button
                      type="button"
                      className={`ep-filter-chip ${logFilter.status === 'all' ? 'is-active' : ''}`}
                      onClick={() => setLogFilter((f) => ({ ...f, status: 'all' }))}
                    >
                      All {logs.length}
                    </button>
                    <button
                      type="button"
                      className={`ep-filter-chip ep-filter-chip-ok ${logFilter.status === 'ok' ? 'is-active' : ''}`}
                      onClick={() => setLogFilter((f) => ({ ...f, status: 'ok' }))}
                    >
                      OK {okCount}
                    </button>
                    <button
                      type="button"
                      className={`ep-filter-chip ep-filter-chip-error ${logFilter.status === 'error' ? 'is-active' : ''}`}
                      onClick={() => setLogFilter((f) => ({ ...f, status: 'error' }))}
                    >
                      Errors {errorCount}
                    </button>
                  </div>
                </div>
                <div className="ep-logs-toolbar-right">
                  <input
                    className="form-input ep-log-search"
                    placeholder="Search method, status, request, response, debug log…"
                    value={logFilter.q}
                    onChange={(e) => setLogFilter((f) => ({ ...f, q: e.target.value }))}
                  />
                  <button
                    type="button"
                    className={`ep-live-toggle ${liveTail ? 'is-live' : ''}`}
                    onClick={() => setLiveTail((v) => !v)}
                    title={liveTail ? 'Auto-refreshing every 4s — click to stop' : 'Auto-refresh this list every 4s'}
                  >
                    <span className="ep-live-dot" />
                    {liveTail ? 'Live' : 'Live tail'}
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={handleDownloadLogs}>Download</button>
                  <button className="btn btn-outline btn-sm" onClick={handleClearLogs}>Clear Logs</button>
                </div>
              </div>
              {logsLoading ? (
                <div className="tab-loading">Loading logs…</div>
              ) : logs.length === 0 ? (
                <div className="tab-empty"><p>No calls logged yet — this endpoint hasn\u2019t been hit.</p></div>
              ) : filteredLogs.length === 0 ? (
                <div className="tab-empty"><p>No logs match this filter.</p></div>
              ) : (
                <div className="ep-log-list">
                  {filteredLogs.map((log) => {
                    const isError = isLogError(log);
                    const isOpen = expandedLog === log.lId;
                    const debugEntries = parseDebugLog(log.lDebugLog);
                    return (
                      <div key={log.lId} className={`ep-log-row ${isError ? 'is-error' : 'is-ok'}`}>
                        <div className="ep-log-row-head" onClick={() => setExpandedLog(isOpen ? null : log.lId)}>
                          <span className={`ep-log-status ${isError ? 'is-error' : 'is-ok'}`}>{log.lStatusCode || '—'}</span>
                          <span className="ep-log-method">{log.lMethod || 'GET'}</span>
                          <span className="ep-log-time">{formatDate(log.lCreatedAt)}</span>
                          {debugEntries.length > 0 && (
                            <span className="ep-log-debug-badge" title="Debug log lines">🖥 {debugEntries.length}</span>
                          )}
                          <span className="ep-log-duration">{log.lDurationMs != null ? `${log.lDurationMs}ms` : ''}</span>
                          <span className="ep-log-chevron">{isOpen ? '▾' : '▸'}</span>
                        </div>
                        {isOpen && (
                          <div className="ep-log-detail">
                            {log.lError && (
                              <div className="ep-log-error">
                                <div className="ep-help-label">Error</div>
                                {log.lError}
                              </div>
                            )}
                            {log.lStackTrace && (
                              <div className="ep-log-error">
                                <div className="ep-help-label">Stack trace</div>
                                <pre className="ep-log-pre">{log.lStackTrace}</pre>
                              </div>
                            )}
                            {debugEntries.length > 0 && (
                              <div className="ep-console">
                                <div className="ep-console-head">
                                  <span className="ep-help-label">Debug Log</span>
                                  <span className="ep-console-count">
                                    {debugEntries.length} line{debugEntries.length === 1 ? '' : 's'}
                                  </span>
                                  <button
                                    type="button"
                                    className="ep-console-copy"
                                    onClick={() => {
                                      copyToClipboard(debugEntries.map(formatDebugLineForExport).join('\n'));
                                      showToast('Debug log copied');
                                    }}
                                  >
                                    Copy
                                  </button>
                                </div>
                                <div className="ep-console-body">
                                  {debugEntries.map((entry, i) => (
                                    <div className="ep-console-line" key={i}>
                                      <span className="ep-console-time">{entry.t != null ? `+${entry.t}ms` : '·'}</span>
                                      <span className={`ep-console-src ep-console-src-${entry.src === 'error_log' ? 'error_log' : 'app'}`}>
                                        {entry.src === 'error_log' ? 'PHP' : 'APP'}
                                      </span>
                                      <span className="ep-console-msg">
                                        {entry.msg}
                                        {entry.data && <pre className="ep-console-data">{entry.data}</pre>}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {log.lWarnings && (
                              <div className="ep-log-warnings">
                                <div className="ep-help-label">PHP warnings / notices</div>
                                <pre className="ep-log-pre">
                                  {(() => {
                                    try {
                                      const arr = JSON.parse(log.lWarnings);
                                      return Array.isArray(arr) ? arr.join('\n') : log.lWarnings;
                                    } catch {
                                      return log.lWarnings;
                                    }
                                  })()}
                                </pre>
                              </div>
                            )}
                            <div className="ep-log-detail-cols">
                              <div>
                                <div className="ep-help-label">Request body</div>
                                <pre className="ep-log-pre">{log.lRequestBody || '(empty)'}</pre>
                              </div>
                              <div>
                                <div className="ep-help-label">Response</div>
                                <pre className="ep-log-pre">{log.lResponseBody || '(empty)'}</pre>
                              </div>
                            </div>
                            <div className="ep-log-detail-cols">
                              <div>
                                <div className="ep-help-label">Query string</div>
                                <pre className="ep-log-pre">{log.lQueryString || '(empty)'}</pre>
                              </div>
                              <div>
                                <div className="ep-help-label">Headers</div>
                                <pre className="ep-log-pre">{log.lHeaders || '(empty)'}</pre>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      {/* ── Confirm modal (replaces window.confirm) ───────────── */}
      {confirmState && (
        <div className="modal-backdrop ep-confirm-backdrop" onClick={() => !confirmState.busy && setConfirmState(null)}>
          <div className="modal ep-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`ep-confirm-icon ${confirmState.danger ? 'is-danger' : 'is-info'}`}>
              {confirmState.danger ? '⚠' : '?'}
            </div>
            <h3 className="ep-confirm-title">{confirmState.title}</h3>
            <p className="ep-confirm-message">{confirmState.message}</p>
            <div className="ep-confirm-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setConfirmState(null)}
                disabled={confirmState.busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ep-confirm-confirm ${confirmState.danger ? 'is-danger' : ''}`}
                onClick={runConfirm}
                disabled={confirmState.busy}
              >
                {confirmState.busy ? 'Working…' : confirmState.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`ep-toast ep-toast-${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}